import { findIndex } from 'lodash'

import i18n from '@/i18n/i18n.js'
import AdministrativeProceedingReminderService from '@/services/admin/AdministrativeProceedingReminderService'
import OrderService from '@/services/admin/OrderService'

import { build_query, errorMessage } from '@/utilities/helpers'

const { t } = i18n.global

export const namespaced = true

export const allowedFilters = [
  'remind_date',
  'reminder_assignee',
  'phone_survey_option_for_administrative_proceeding_reminder',
  'fullname',
  'phone'
]

export const state = {
  proceedingReminders: [],
  proceedingReminder: {},
  meta: null,
  reminderLoading: false
}

export const mutations = {
  SET_PROCEEDING_CALLS(state, proceedingReminders) {
    state.proceedingReminders = proceedingReminders.data
    state.meta = proceedingReminders.meta
  },
  ADD_PROCEEDING_CALLS(state, proceedingReminders) {
    state.proceedingReminders.push(...proceedingReminders.data)
    state.meta = proceedingReminders.meta
  },
  CLEAR_PROCEEDING_CALL(state) {
    state.proceedingReminder = {}
  },
  SET_PROCEEDING_CALL(state, proceedingReminder) {
    state.proceedingReminder = proceedingReminder
  },
  SET_REMINDER_LOADING(state, loading) {
    state.reminderLoading = loading
  },
  DELETE_PAYMENT_PROMISE(state, paymentPromiseId) {
    const paymentPromises = state.proceedingReminder.order.payment_promises.filter(promise => {
      return promise.id !== paymentPromiseId
    })

    state.proceedingReminder.order.payment_promises = paymentPromises
  },
  DELETE_PROCEEDING_CALL(state, proceedingReminderToRemove) {
    state.proceedingReminders = state.proceedingReminders.filter(reminder => {
      return reminder.id !== proceedingReminderToRemove.id
    })
  },
  UPDATE_REMINDER(state, proceedingReminderArrayParam) {
    let position = -1

    proceedingReminderArrayParam.forEach(proceedingReminder => {
      position = findIndex(state.proceedingReminders, r => {
        return r.id === proceedingReminder.id
      })

      if (position !== -1) {
        let newReminder = state.proceedingReminders[position]
        if (proceedingReminder.assignee) {
          newReminder.assignee = proceedingReminder.assignee
        } else {
          newReminder.assignee = null
        }

        state.proceedingReminders.splice(position, 1, newReminder)
      }
    })

    const matchingReminder = proceedingReminderArrayParam.find(r => r.id === state.proceedingReminder.id)

    if (matchingReminder) {
      if (matchingReminder.assignee) {
        state.proceedingReminder.assignee = matchingReminder.assignee
      } else {
        state.proceedingReminder.assignee = null
      }

      let selectedIndex = findIndex(state.proceedingReminders, r => {
        return r.id === state.proceedingReminder.id
      })

      if (selectedIndex !== -1) {
        state.proceedingReminders.splice(selectedIndex, 1, { ...state.proceedingReminder })
      }
    }
  }
}

export const actions = {
  getAdministrativeProceedingReminders({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters)
    return new Promise((resolve, reject) => {
      AdministrativeProceedingReminderService.getAdministrativeProceedingReminders(newQuery)
        .then(response => {
          commit('SET_PROCEEDING_CALLS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message:
              t('views.admin.administrative_proceeding_calls.get_administrative_proceeding_reminder_error') +
              ': ' +
              errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  getAdministrativeProceedingReminder({ commit, dispatch }, reminderId) {
    return new Promise((resolve, reject) => {
      commit('SET_REMINDER_LOADING', true)
      AdministrativeProceedingReminderService.getAdministrativeProceedingReminder(reminderId)
        .then(response => {
          response.data.id = reminderId
          commit('SET_PROCEEDING_CALL', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message:
              t('views.admin.administrative_proceeding_calls.get_administrative_proceeding_reminder_error') +
              ': ' +
              errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
        .finally(() => {
          commit('SET_REMINDER_LOADING', false)
        })
    })
  },
  loadAdministrativeProceedingReminder({ commit, state }, query) {
    query.page = state.meta.current_page + 1
    let newQuery = build_query(query, allowedFilters)
    return new Promise(resolve => {
      AdministrativeProceedingReminderService.getAdministrativeProceedingReminders(newQuery)
        .then(response => {
          commit('ADD_PROCEEDING_CALLS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message:
              t('views.admin.administrative_proceeding_calls.load_administrative_proceeding_reminder_error') +
              ': ' +
              errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  assignOperation({ commit, dispatch }, administrativeReminder) {
    return new Promise((resolve, reject) => {
      AdministrativeProceedingReminderService.assign(administrativeReminder)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.assigned', {
              element: t('views.admin.administrative_proceeding_calls.administrative_proceeding_calls')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          commit('UPDATE_REMINDER', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('views.admin.service_errors.error') + ': ' + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getPaymentPromises({ dispatch, commit }, id) {
    return new Promise((resolve, reject) => {
      OrderService.getPaymentPromises(id)
        .then(response => {
          commit('SET_PAYMENT_PROMISES', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deletePaymentPromise({ dispatch, state, commit }, paymentPromiseId) {
    return new Promise((resolve, reject) => {
      OrderService.deletePaymentPromise(state.proceedingReminder.order_id, paymentPromiseId)
        .then(response => {
          commit('DELETE_PAYMENT_PROMISE', paymentPromiseId)
          const notification = {
            type: 'warning',
            message: t('store.admin.order.delete_payment_promise_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.order.error_while_deleting_payment_promise') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  saveSurveyAnswers({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      AdministrativeProceedingReminderService.saveSurveyAnswers(payload.reminder_call_id, payload)
        .then(response => {
          commit('SET_PROCEEDING_CALL', response.data)
          const notification = {
            type: 'success',
            message: 'Anket cevapları kaydedildi'
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: 'Anket cevapları kaydedilirken hata oluştu'
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
